import React, { useMemo } from 'react';
import {
  Box,
  capitalizeFirstLetter,
  CoveragePeriods,
  EnrollmentStatus,
  Text,
  TextWithParagraphs,
} from '@common-fe/common-fe';

import { ROLLOVER_BLOCK_TEXT } from '@/modules/employee/employee.constants.ts';

import { getEnrollmentStatusColor } from '../../Employee/PersonalInformation/EmploymentStatusTimeline/utils';

import EligibleServicesField from './EligibleServices';
import { EnrollmentCoverageInfoHint, LimitedPeriods } from './EnrollmentCoverageInfoHint';

interface Props {
  coverageDateStart?: string;
  coverageDateEnd?: string;
  coveragePeriods?: CoveragePeriods[];
  submitClaimsDateEnd?: string;
  enrollmentStatus?: string;
  limitedPeriods?: LimitedPeriods[];
  externalDeductibleMet?: string;
  isRollover?: boolean;
  notes?: string;
  hasAccessToNotes?: boolean;
  policyId?: number | string;
  accountName?: string;
  accountType?: string;
}

export const EnrollmentDetailsEnrollmentStatus: React.FC<Props> = ({
  coverageDateStart,
  coverageDateEnd,
  coveragePeriods,
  submitClaimsDateEnd,
  enrollmentStatus,
  limitedPeriods,
  externalDeductibleMet,
  isRollover,
  notes,
  hasAccessToNotes,
  policyId,
  accountName,
  accountType,
}) => {
  const statusColor = useMemo(
    () => getEnrollmentStatusColor(enrollmentStatus as EnrollmentStatus),
    [enrollmentStatus]
  );
  return (
    <Box
      pad="spacing24"
      background="canvas"
      round="container1Round"
      border={{ size: '1px', color: 'border2' }}
      gap="spacing16"
    >
      <Box direction="row" height={{ min: '40px', max: '40px' }} align="center">
        <Box width={{ max: '256px', min: '256px' }}>
          <Text size="medium" weight={700} style={{ lineHeight: '20px' }}>
            Enrollment
          </Text>
        </Box>
        <Box direction="row" justify="between" height={{ max: '24px' }}>
          <Text size="medium" weight={400} color={statusColor}>
            {capitalizeFirstLetter(enrollmentStatus || '-')}
          </Text>
        </Box>
      </Box>

      <Box direction="row" height={{ min: '40px', max: '40px' }} align="center">
        <Box width={{ max: '256px', min: '256px' }}>
          <Text size="medium" weight={700} style={{ lineHeight: '20px' }}>
            Main coverage period
          </Text>
        </Box>
        <Box direction="row" justify="between" height={{ max: '24px' }}>
          <Text size="medium" weight={400}>
            {coverageDateStart ? `${coverageDateStart} - ` : ''}
            {coverageDateEnd || 'No deadline'}
          </Text>
        </Box>

        {(limitedPeriods && limitedPeriods.length) || externalDeductibleMet ? (
          <Box width={{ max: '16px', min: '16px' }} margin={{ left: 'spacing24' }}>
            <EnrollmentCoverageInfoHint
              limitedPeriods={limitedPeriods}
              externalDeductibleMet={externalDeductibleMet}
              coveragePeriods={coveragePeriods}
            />
          </Box>
        ) : null}
      </Box>

      <Box direction="row" height={{ min: '40px', max: '40px' }} align="center">
        <Box width={{ max: '256px', min: '256px' }}>
          <Text size="medium" weight={700} style={{ lineHeight: '20px' }}>
            Claim submit deadline
          </Text>
        </Box>
        <Box direction="row" justify="between" height={{ max: '24px' }}>
          <Text size="medium" weight={400}>
            {submitClaimsDateEnd || 'No deadline'}
          </Text>
        </Box>
      </Box>

      <EligibleServicesField policyId={policyId} accountName={accountName} accountType={accountType} />

      {isRollover && (
        <Box direction="row" height={{ min: '40px', max: '40px' }} align="center">
          <Box width={{ max: '256px', min: '256px' }}>
            <Text size="medium" weight={700} style={{ lineHeight: '20px' }}>
              Rollover
            </Text>
          </Box>
          <Box direction="row" justify="between" height={{ max: '24px' }}>
            <Text size="medium" weight={400}>
              {ROLLOVER_BLOCK_TEXT}
            </Text>
          </Box>
        </Box>
      )}
      {notes && hasAccessToNotes && (
        <Box direction="row" height={{ min: '40px' }} align="start">
          <Box width={{ max: '256px', min: '256px' }}>
            <Text size="medium" weight={700} style={{ lineHeight: '20px' }}>
              Plan notes
            </Text>
          </Box>
          <Box direction="row" justify="between">
            <TextWithParagraphs text={notes} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
